import type {
    MSRCODataResponse,
    MSRCUpdateSummary,
    MSRCUpdatesQueryOptions,
} from '../types/msrc-cvrf';
import { CvrfDocumentInfo } from '../types/msrc-cvrf';

/**
 * Base URL for MSRC CVRF API v3.
 * Override with REACT_APP_MSRC_CVRF_API_URL if using a proxy (e.g. for CORS).
 */
const MSRC_CVRF_BASE =
    typeof process !== 'undefined' && process.env?.REACT_APP_MSRC_CVRF_API_URL
        ? process.env.REACT_APP_MSRC_CVRF_API_URL
        : 'https://smartdataproxy.azurewebsites.net/api/CachedProxy?target=https://api.msrc.microsoft.com/cvrf/v3.0';

/**
 * Client for the Microsoft Security Response Center (MSRC) CVRF API v3.
 * @see https://api.msrc.microsoft.com/cvrf/v3.0/swagger/v3/swagger.json
 *
 * - GET /Updates – list of security update summaries (supports OData $filter, $orderby, $skip; $top not supported by API).
 * - GET /Updates(key) – summary by update ID (yyyy-mmm), CVE ID, or year (yyyy).
 * - GET /cvrf/{id} – full CVRF document (id = yyyy-mmm).
 * - GET /csaf/{id} – CSAF document by id.
 */
export class MSRCCvrfManager {
    private static instance: MSRCCvrfManager;

    private constructor() {}

    static getInstance(): MSRCCvrfManager {
        if (!MSRCCvrfManager.instance) {
            MSRCCvrfManager.instance = new MSRCCvrfManager();
        }
        return MSRCCvrfManager.instance;
    }

    /**
     * Builds query string from OData-style options.
     * Note: This API does not allow $top (server limit 0); use client-side slicing for count.
     */
    private buildQueryString(options?: MSRCUpdatesQueryOptions): string {
        if (!options) return '';
        const params = new URLSearchParams();
        if (options.filter != null && options.filter !== '') {
            params.set('$filter', options.filter);
        }
        if (options.orderBy != null && options.orderBy !== '') {
            params.set('$orderby', options.orderBy);
        }
        // $top is rejected by this API (limit 0); slice results client-side instead.
        if (options.skip != null && options.skip > 0) {
            params.set('$skip', String(options.skip));
        }
        if (options.select != null && options.select !== '') {
            params.set('$select', options.select);
        }
        const qs = params.toString();
        return qs ? `?${qs}` : '';
    }

    /**
     * GET /Updates – list of all security update summaries.
     * Supports OData URL filtering on current and initial release dates.
     */
    async getUpdates(options?: MSRCUpdatesQueryOptions): Promise<MSRCUpdateSummary[]> {
        const qs = this.buildQueryString(options);
        const url = `${MSRC_CVRF_BASE}/Updates${qs}`;
        const response = await fetch(url, {
            headers: { Accept: 'plain/text' },
        });
        if (!response.ok) {
            throw new Error(`MSRC CVRF API error: ${response.status} ${response.statusText}`);
        }
        const data: MSRCODataResponse<MSRCUpdateSummary> = JSON.parse(await response.text());
        const list = data.value ?? [];
        list.sort((a, b) => {
            const dateA = a.CurrentReleaseDate ? new Date(a.CurrentReleaseDate).getTime() : 0;
            const dateB = b.CurrentReleaseDate ? new Date(b.CurrentReleaseDate).getTime() : 0;
            return dateB - dateA;
        });
        return list;
    }

    /**
     * GET /Updates('key') – security update summary by key.
     * Key can be: update ID (yyyy-mmm), vulnerability ID (CVE number), or year (yyyy).
     */
    async getUpdateByKey(key: string): Promise<MSRCUpdateSummary | null> {
        const encoded = encodeURIComponent(key);
        const url = `${MSRC_CVRF_BASE}/Updates('${encoded}')`;
        const response = await fetch(url, {
            headers: { Accept: 'application/json' },
        });
        if (response.status === 404) return null;
        if (!response.ok) {
            throw new Error(`MSRC CVRF API error: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();
        return (data as MSRCUpdateSummary) ?? null;
    }

    /**
     * GET /cvrf/{id} – full security update details in CVRF format.
     * Endpoint returns plain/text; the response body is XML.
     * @param id CVRF document ID (yyyy-mmm, e.g. 2025-Jan).
     * @returns Parsed CvrfDocumentInfo from the XML body.
     */
    async getCvrfDocument(id: string): Promise<CvrfDocumentInfo> {
        const encoded = encodeURIComponent(id);
        const url = `${MSRC_CVRF_BASE}/cvrf/${encoded}`;
        const response = await fetch(url, {
            headers: { Accept: 'text/plain' },
        });
        if (!response.ok) {
            throw new Error(`MSRC CVRF API error: ${response.status} ${response.statusText}`);
        }
        const xmlText = await response.text();
        return CvrfDocumentInfo.parseFromXml(xmlText);
    }

    /**
     * GET /csaf/{id} – CSAF document by id.
     */
    async getCsafDocument(id: string, accept: string = 'application/json'): Promise<unknown> {
        const encoded = encodeURIComponent(id);
        const url = `${MSRC_CVRF_BASE}/csaf/${encoded}`;
        const response = await fetch(url, {
            headers: { Accept: accept },
        });
        if (!response.ok) {
            throw new Error(`MSRC CVRF API error: ${response.status} ${response.statusText}`);
        }
        const contentType = response.headers.get('content-type') ?? '';
        if (contentType.includes('application/json')) {
            return response.json();
        }
        return response.text();
    }
}
